import { Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib/cn'

import { useDeleteAllReadMutation } from '../hooks/useNotifications'
import type { NotificationTab } from '../types/notification.types'

interface DeleteAllReadButtonProps {
  /**
   * Tamaño visual del botón:
   *   - {@code 'md'} (default): pill brutalista con icono+texto, para la cabecera
   *     de {@code /notifications}.
   *   - {@code 'sm'}: pill compacto solo-icono+texto pequeño, para el dropdown
   *     del bell donde el espacio es escaso.
   */
  size?: 'sm' | 'md'
  /**
   * Si {@code true}, el botón se renderiza deshabilitado (visual + pointer-events).
   * Útil para reservar slot fijo en la cabecera cuando no hay items leídos —
   * evita que el botón "Limpiar leídas" salte de posición al desaparecer.
   */
  disabled?: boolean
  /** Pestaña sobre la que actúa; sin ella, todos los avisos. */
  tab?: NotificationTab
}

/**
 * Botón "Limpiar leídas" con confirmación inline (sin modal). Primer click
 * cambia copy + color a estado "¿Seguro?"; segundo click dispara la
 * mutación. Si en 3s no hay segundo click, vuelve al estado base — evita
 * que el usuario olvide que pulsó y borre por accidente al volver.
 */
export function DeleteAllReadButton({
  size = 'md',
  disabled = false,
  tab,
}: DeleteAllReadButtonProps) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const deleteAll = useDeleteAllReadMutation(tab)
  const timeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    }
  }, [])

  // Si el botón se deshabilita externamente mientras está en estado "confirming",
  // cancelar la confirmación pendiente para no dejar UI inconsistente.
  useEffect(() => {
    if (disabled && confirming) {
      setConfirming(false)
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [disabled, confirming])

  function handleClick() {
    if (!confirming) {
      setConfirming(true)
      timeoutRef.current = window.setTimeout(() => setConfirming(false), 3000)
      return
    }
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setConfirming(false)
    deleteAll.mutate()
  }

  const isDisabled = disabled || deleteAll.isPending

  const baseClass =
    size === 'md'
      ? 'brutal-sm brutal-press inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-brutal text-[10px] font-bold uppercase tracking-widest disabled:cursor-not-allowed disabled:opacity-40'
      : 'brutal-sm brutal-press inline-flex items-center gap-1 rounded-md px-2 py-1 font-brutal text-[10px] font-bold uppercase tracking-widest disabled:cursor-not-allowed disabled:opacity-40'

  const stateClass = confirming
    ? 'bg-red text-white'
    : 'bg-background-alt text-foreground hover:bg-yellow/30'

  const iconSize = size === 'md' ? 14 : 12

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      className={cn(baseClass, stateClass)}
    >
      <Trash2 size={iconSize} aria-hidden="true" strokeWidth={2.5} />
      <span>
        {confirming
          ? t('notifications.actions.confirmDelete')
          : t('notifications.actions.deleteAllRead')}
      </span>
    </button>
  )
}
